import { consola } from 'consola'
import { useApi } from '@shared/api'
import type { ComboSelectionModifiers } from '@/utils/comboSelections'

const logger = consola.withTag('api-cart')

// --- Types ---

export interface BaseCartProduct {
  id: string
  name: string
  quantity: number
  price: number
  img: string[]
  category_id?: string
  is_favorite?: boolean
  teg?: string
}

export interface CartProductAPI extends BaseCartProduct {
  itemType: 'product'
  productId: string
  addons?: string[]
  excludes?: string[]
  size?: string
}

export interface CartComboAPI extends BaseCartProduct {
  itemType: 'combo'
  comboId: string
  comboSelections: Array<{
    group_id: string | number
    product_ids: (string | number)[]
  }>
}

/** @deprecated Use CartProductAPI | CartComboAPI */
export interface CartProductLegacy extends BaseCartProduct {
  addons?: string[]
  excludes?: string[]
  size?: string
}

export type CartProduct = CartProductAPI | CartComboAPI | CartProductLegacy

export interface CartResponse {
  products: CartProduct[]
}

export interface AddToCartPayload {
  product_id: string
  branch_id: string
  quantity: number
  addons?: string[]
  excludes?: string[]
}

/** Выбор товара в группе комбо + кастомизация слота («Изменить состав») */
export interface ComboSelectionPayload {
  group_id: string | number
  product_ids: (string | number)[]
  modifiers?: ComboSelectionModifiers[]
}

export interface CartItemPayload {
  branch_id: string
  amount?: number
  item_type?: 'product' | 'combo'
  product_id?: string
  addons?: string[]
  excludes?: string[]
  combo_id?: number
  combo_selections?: ComboSelectionPayload[]
}

export interface AddComboToCartPayload {
  combo_id: string
  branch_id: string
  quantity: number
  selections: ComboSelectionPayload[] | Record<string | number, string | number>
}

export interface AddBundleToCartPayload {
  branch_id: number
  base_product_id: number
  bundle_id: number
  step_selections: Array<{ step_id: number; product_id: number }>
}

// --- Helpers ---

/** Модификаторы слота в формате бэка; пустые не отправляем */
const toComboModifiersBody = (modifiers?: ComboSelectionModifiers[]) => {
  const normalized = (modifiers ?? [])
    .map(m => ({
      product_id: Number(m.product_id),
      addons: (m.addons ?? []).map(Number),
      excludes: (m.excludes ?? []).map(Number),
    }))
    .filter(m => m.addons.length > 0 || m.excludes.length > 0)

  return normalized.length > 0 ? normalized : undefined
}

const toComboSelectionBody = (sel: ComboSelectionPayload) => {
  const modifiers = toComboModifiersBody(sel.modifiers)

  return {
    group_id: Number(sel.group_id),
    product_ids: Array.isArray(sel.product_ids)
      ? (sel.product_ids as any[]).map(Number)
      : [Number(sel.product_ids)],
    ...(modifiers ? { modifiers } : {}),
  }
}

const normalizeComboSelections = (selections: AddComboToCartPayload['selections']) => {
  const arr: ComboSelectionPayload[] = Array.isArray(selections)
    ? selections
    : Object.entries(selections).map(([groupId, productId]) => ({
        group_id: groupId,
        product_ids: [productId],
      }))

  return arr.map(toComboSelectionBody)
}

const buildCartItemBody = (payload: CartItemPayload): Record<string, any> => {
  const isCombo = payload.item_type === 'combo' || !!payload.combo_id

  const base: Record<string, any> = {
    branch_id: String(payload.branch_id),
    amount: payload.amount ?? 1,
  }

  if (isCombo) {
    return {
      ...base,
      item_type: 'combo',
      combo_id: Number(payload.combo_id),
      combo_selections: payload.combo_selections?.map(toComboSelectionBody),
    }
  }

  return {
    ...base,
    item_type: 'product',
    product_id: String(payload.product_id),
    addons: payload.addons ?? [],
    excludes: payload.excludes ?? [],
  }
}

// --- API class ---

/**
 * API-клиент корзины пользователя.
 * Базовый URL: /api/v2/cart
 */
class APICart {
  /**
   * Получить корзину пользователя.
   * GET /api/v2/cart
   */
  async getCart(branchId: number | string): Promise<CartResponse> {
    logger.debug('Getting cart', { branchId })
    const response = await useApi(true).get('/cart', {
      params: { branch_id: String(branchId) },
    })
    logger.success('Cart received', { count: response.data?.products?.length })
    return response.data
  }

  /**
   * Добавить товар в корзину (quantity работает как дельта, не абсолютное значение).
   * POST /api/v2/cart
   */
  async addToCart(payload: AddToCartPayload): Promise<{ message: string; cart: any }> {
    logger.debug('Adding to cart', { productId: payload.product_id, qty: payload.quantity })
    const response = await useApi(true).post('/cart', {
      body: {
        product_id: String(payload.product_id),
        branch_id: String(payload.branch_id),
        quantity: payload.quantity,
        addons: payload.addons ?? [],
        excludes: payload.excludes ?? [],
      },
    })
    logger.success('Added to cart', { productId: payload.product_id })
    return response.data
  }

  /**
   * Увеличить количество товара.
   * PUT /api/v2/cart/item/increase
   */
  async increaseQuantity(payload: CartItemPayload): Promise<{ message: string; cart: any }> {
    const response = await useApi(true).put('/cart/item/increase', { body: buildCartItemBody(payload) })
    return response.data
  }

  /**
   * Уменьшить количество товара.
   * PUT /api/v2/cart/item/decrease
   */
  async decreaseQuantity(payload: CartItemPayload): Promise<{ message: string; cart: any; removed: boolean }> {
    const response = await useApi(true).put('/cart/item/decrease', { body: buildCartItemBody(payload) })
    return response.data
  }

  /**
   * Удалить товар из корзины.
   * DELETE /api/v2/cart/item
   */
  async removeFromCart(payload: CartItemPayload): Promise<{ message: string }> {
    const body = buildCartItemBody(payload)
    delete body.amount
    const response = await useApi(true).delete('/cart/item', { body })
    return response.data
  }

  /**
   * Очистить корзину.
   * DELETE /api/v2/cart
   */
  async clearCart(branchId: number | string): Promise<{ message: string }> {
    logger.info('Clearing cart', { branchId })
    const response = await useApi(true).delete('/cart', {
      body: { branch_id: String(branchId) },
    })
    logger.success('Cart cleared')
    return response.data
  }

  /**
   * Добавить комбо-товар в корзину.
   * POST /api/v2/cart/combo
   */
  async addComboToCart(payload: AddComboToCartPayload): Promise<{ message: string; cart: any }> {
    logger.debug('Adding combo to cart', { comboId: payload.combo_id, qty: payload.quantity })

    const response = await useApi(true).post('/cart/combo', {
      body: {
        combo_id: Number(payload.combo_id),
        branch_id: Number(payload.branch_id),
        quantity: Number(payload.quantity),
        selections: normalizeComboSelections(payload.selections),
      },
    })

    logger.success('Combo added to cart', { comboId: payload.combo_id })
    return response.data
  }

  /**
   * Добавить собранный набор («Собрать набор») в корзину.
   * POST /api/v2/cart/bundle
   *
   * Сервер раскладывает набор в обычные товары — отдельной сущности в корзине нет.
   */
  async addBundleToCart(payload: AddBundleToCartPayload): Promise<{ message: string; cart: any }> {
    logger.debug('Adding bundle to cart', {
      bundleId: payload.bundle_id,
      baseProductId: payload.base_product_id,
      steps: payload.step_selections.length,
    })

    const response = await useApi(true).post('/cart/bundle', {
      body: {
        branch_id: Number(payload.branch_id),
        base_product_id: Number(payload.base_product_id),
        bundle_id: Number(payload.bundle_id),
        step_selections: payload.step_selections.map(s => ({
          step_id: Number(s.step_id),
          product_id: Number(s.product_id),
        })),
      },
    })

    logger.success('Bundle added to cart', { bundleId: payload.bundle_id })
    return response.data
  }
}

export const apiCart = new APICart()
export default apiCart
